/**
 * Dialog for starting a new outbound WhatsApp conversation.
 *
 * Lets the business user pick a WhatsApp-enabled persona, enter
 * a customer phone number, and send an initial outbound message.
 */

import { useState, useEffect } from 'react'
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalDescription,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Textarea,
  Alert,
  AlertDescription,
} from '@/components/ui'
import { cn } from '@/utils'
import { usePersonaStore } from '@/stores'
import { Send, Check, Info } from 'lucide-react'
import type { Persona } from '@/stores'
import type { ConflictDetail } from '@/types/inbox'

interface NewConversationDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (data: {
    personaId: string
    customerPhone: string
    customerDisplayName?: string
    initialMessage: string
    confirmMultiplePersonas?: boolean
  }) => Promise<{ ok: true; conversationId: string } | { ok: false; conflict?: ConflictDetail; error?: string }>
  /** Pre-fill customer phone (e.g. when opened from conversation detail). */
  defaultCustomerPhone?: string
  /** Pre-fill customer name. */
  defaultCustomerName?: string
}

export function NewConversationDialog({
  open,
  onOpenChange,
  onSubmit,
  defaultCustomerPhone,
  defaultCustomerName,
}: NewConversationDialogProps) {
  const { personas } = usePersonaStore()

  // Filter to WhatsApp-enabled personas
  const whatsappPersonas = Object.values(personas).filter(
    (p) => p.whatsappConfig?.enabled
  )

  const [selectedPersonaId, setSelectedPersonaId] = useState<string | null>(null)
  const [customerPhone, setCustomerPhone] = useState('')
  const [customerName, setCustomerName] = useState('')
  const [message, setMessage] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [conflictWarning, setConflictWarning] = useState<ConflictDetail | null>(null)

  const hasCustomerDefaults = !!(defaultCustomerPhone)

  // Reset when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedPersonaId(whatsappPersonas.length === 1 ? whatsappPersonas[0].id : null)
      setCustomerPhone(defaultCustomerPhone || '')
      setCustomerName(defaultCustomerName || '')
      setMessage('')
      setError(null)
      setConflictWarning(null)
      setIsSubmitting(false)
    }
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  const canSubmit =
    selectedPersonaId && customerPhone.trim().length >= 10 && message.trim().length > 0

  const handleSubmit = async (confirmMultiple = false) => {
    if (!canSubmit) return
    setIsSubmitting(true)
    setError(null)

    const result = await onSubmit({
      personaId: selectedPersonaId,
      customerPhone: customerPhone.trim(),
      customerDisplayName: customerName.trim() || undefined,
      initialMessage: message.trim(),
      confirmMultiplePersonas: confirmMultiple,
    })

    setIsSubmitting(false)

    if (result.ok) {
      onOpenChange(false)
    } else if (result.conflict) {
      if (result.conflict.code === 'active_conversation_exists') {
        setError(result.conflict.message)
      } else if (result.conflict.code === 'other_persona_active') {
        setConflictWarning(result.conflict)
      }
    } else if (result.error) {
      setError(result.error)
    }
  }

  const handleConfirmMultiplePersonas = () => {
    setConflictWarning(null)
    handleSubmit(true)
  }

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent size="md">
        <ModalHeader>
          <ModalTitle>New WhatsApp Conversation</ModalTitle>
          <ModalDescription>
            Start a fresh conversation with a WhatsApp customer.
          </ModalDescription>
        </ModalHeader>

        <ModalBody>
          <div className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* Multi-persona warning */}
            {conflictWarning && (
              <Alert variant="warning">
                <div className="space-y-2">
                  <div>
                    <p className="text-sm font-medium">{conflictWarning.message}</p>
                    {conflictWarning.existing_conversations && (
                      <ul className="mt-1 text-xs space-y-0.5">
                        {conflictWarning.existing_conversations.map((c) => (
                          <li key={c.conversation_id}>
                            {c.persona_name} ({c.status})
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => setConflictWarning(null)}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      variant="default"
                      onClick={handleConfirmMultiplePersonas}
                      isLoading={isSubmitting}
                    >
                      Continue Anyway
                    </Button>
                  </div>
                </div>
              </Alert>
            )}

            {/* Persona selection */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Persona
              </label>
              {whatsappPersonas.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No WhatsApp-enabled personas found. Enable WhatsApp in a persona's Channels tab first.
                </p>
              ) : (
                <div className="space-y-1.5 max-h-40 overflow-y-auto">
                  {whatsappPersonas.map((persona) => (
                    <PersonaOption
                      key={persona.id}
                      persona={persona}
                      isSelected={selectedPersonaId === persona.id}
                      onSelect={() => setSelectedPersonaId(persona.id)}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Phone number */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Customer Phone Number
              </label>
              <Input
                type="tel"
                placeholder="+1234567890"
                value={customerPhone}
                onChange={(e) => setCustomerPhone(e.target.value)}
                readOnly={hasCustomerDefaults}
                className={cn('text-sm', hasCustomerDefaults && 'bg-muted cursor-not-allowed')}
              />
              {!hasCustomerDefaults && (
                <p className="text-xs text-muted-foreground mt-1">
                  E.164 format with country code (e.g. +1 for US)
                </p>
              )}
            </div>

            {/* Display name (optional) */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Customer Name <span className="text-muted-foreground font-normal">(optional)</span>
              </label>
              <Input
                type="text"
                placeholder="John Doe"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                readOnly={hasCustomerDefaults}
                className={cn('text-sm', hasCustomerDefaults && 'bg-muted cursor-not-allowed')}
              />
            </div>

            {/* Message */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Message
              </label>
              <Textarea
                placeholder="Type your message..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={3}
                className="text-sm resize-none"
              />
            </div>

            {/* 24h window notice */}
            <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-info/10 text-xs text-info">
              <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>
                WhatsApp may reject messages if the customer hasn't contacted this
                number in the last 24 hours. Template messages are not yet supported.
              </span>
            </div>
          </div>
        </ModalBody>

        <ModalFooter>
          <Button
            variant="secondary"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={() => handleSubmit(false)}
            disabled={!canSubmit || isSubmitting || !!conflictWarning}
            isLoading={isSubmitting}
            leftIcon={isSubmitting ? undefined : <Send className="w-4 h-4" />}
          >
            {isSubmitting ? 'Sending...' : 'Send Message'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

function PersonaOption({
  persona,
  isSelected,
  onSelect,
}: {
  persona: Persona
  isSelected: boolean
  onSelect: () => void
}) {
  return (
    <button
      onClick={onSelect}
      className={cn(
        'w-full flex items-center gap-2.5 px-3 py-2 rounded-lg border transition-colors text-left',
        isSelected
          ? 'border-primary bg-primary/10'
          : 'border-border hover:bg-accent'
      )}
    >
      {persona.avatarUrl ? (
        <img
          src={persona.avatarUrl}
          alt={persona.name}
          className="w-8 h-8 rounded-full object-cover shrink-0"
        />
      ) : (
        <div className="w-8 h-8 rounded-full bg-linear-to-br from-primary/80 to-primary flex items-center justify-center text-primary-foreground text-sm font-medium shrink-0">
          {persona.name.charAt(0)}
        </div>
      )}
      <span className="text-sm font-medium text-foreground truncate flex-1">
        {persona.name}
      </span>
      {isSelected && <Check className="w-4 h-4 text-primary shrink-0" />}
    </button>
  )
}
